import { Box, Checkbox, FormControlLabel, FormGroup, Tooltip, Typography } from "@mui/material";
import React from "react";

import { ArtifactType } from "../../../types/artifact";

interface ArtifactTypeSelectorProps {
  selectedTypes: ArtifactType[];
  onChange: (newTypes: ArtifactType[]) => void;
  disabled?: boolean;
}

interface ArtifactTypeOption {
  type: ArtifactType;
  label: string;
  tooltip: string;
}

const LAST_SELECTED_COUNT = 1;

const artifactTypeOptions: ArtifactTypeOption[] = [
  {
    type: ArtifactType.Summary,
    label: "Summary",
    tooltip: "Include conversation summary which provides an overview of the call"
  },
  {
    type: ArtifactType.Scorecard,
    label: "Scorecard",
    tooltip: "Include scorecard data with agent performance metrics"
  },
  {
    type: ArtifactType.CallDataRecord,
    label: "Call Data Record",
    tooltip: "Include call metadata like duration, participants, and timestamps"
  },
  {
    type: ArtifactType.Transcript,
    label: "Transcript",
    tooltip: "Include the full conversation transcript (increases token usage significantly)"
  }
];

export const ArtifactTypeSelector: React.FC<ArtifactTypeSelectorProps> = ({
  selectedTypes,
  onChange,
  disabled = false
}) => {
  const handleChange = (type: ArtifactType) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && !selectedTypes.includes(type)) {
      onChange([...selectedTypes, type]);
      return;
    }
    if (!event.target.checked) {
      onChange(selectedTypes.filter(t => t !== type));
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Tooltip
        title="Each artifact type adds tokens to the context. The token limit is 180,000. More tokens per artifact means fewer artifacts can be analyzed at once."
        placement="top"
      >
        <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "text.secondary", cursor: "help" }}>
          Data to include in analysis (more data = fewer calls):
        </Typography>
      </Tooltip>
      <FormGroup row sx={{ flexWrap: "wrap" }}>
        {artifactTypeOptions.map((option) => {
          const isLastSelectedType = selectedTypes.length === LAST_SELECTED_COUNT && selectedTypes.includes(option.type);
          return (
            <Tooltip key={option.type} title={option.tooltip} placement="top">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectedTypes.includes(option.type)}
                    onChange={handleChange(option.type)}
                    disabled={disabled || isLastSelectedType}
                  />
                }
                label={<Typography variant="caption">{option.label}</Typography>}
                sx={{ minWidth: "50%", mr: 0 }}
              />
            </Tooltip>
          );
        })}
      </FormGroup>
    </Box>
  );
};

export default ArtifactTypeSelector;
